import { Guid } from "guid-typescript";
import yup from "../../../../shared/validation/yupExtensions";
import { FilteredDropDownModel, GroupListModel, ListModel } from "shared/interfaces/core-interfaces";
import { Dayjs } from "shared/dayjs";
import { CreatePlanningTemplateRequest, PlanningTemplateResponse } from "features/Templates/state/template-intefaces";

export interface ProjectSliceModel {
    statusCode: string,
    isLoading: boolean,
    isModalLoading: boolean,
    message: string,
    showNotification: boolean,
    reloadData: boolean,
    listOfActiveProjects: ProjectModelForList[] | [],
    listOfInactiveProjects: ProjectModelForList[] | [],
    projectBasicInfo: ProjectBasicInfoResponse | null,
    procedureTabData: { projectProcedures: ProcedureListModel[] | [], procedureToShow: ProcedureResponse | null }
    projectPlanning: ProjectPlanningResponse | null;
    responsibilityAssignment: CostCenterResponsibilityAssignment;
    projectAssignments: projectAssignmentTableModel[] | [];
    projectInvoicesReport: ProjectFinanceReportResponse | null;
    dropDownLists: DropDownProjectsLists | null;
    projectLocations: ProjectLocation[] | [];
    reloadLocations: boolean;
    projectLocation: ProjectLocationRequest | null,
    filteredPartnerProjectLeadList: FilteredDropDownModel,
    filteredPartnerDeputyProjectLeadList: FilteredDropDownModel,
    filteredPartnerSalesPersonList: FilteredDropDownModel,
    filteredPartnerAccountingContactList: FilteredDropDownModel,
    filteredExternalIntermediaryList: FilteredDropDownModel,
    participantsList: GetProjectParticipantsResponse | null,
    filteredPartnersList: FilteredDropDownModel
}

export interface ProjectModelForList {
    id: Guid | null,
    projectName: string,
    projectLongName: string,
    projectNumber: string,
    departmentId: Guid | null,
    departmentName: string,
    description: string,
    IsDepartmanProject: boolean,
    centarType: string,
}

export interface GetProjects {
    projectNumber: string,
    projects: ProjectModelForList[]
}

export interface ProjectPlanningResponse extends PlanningTemplateResponse {
    projectId: string | null;
    project: string | null;
    sourceTemplateId: string | null;
}

export interface PartnersWithTypes<T> {
    value: T,
    label: string,
    items: ListModel<T>[]
}

export interface DropDownProjectsLists {
    projectNumber: string,
    tenants: ListModel<string>[] | [],
    departments: ListModel<string>[] | [],
    articles: ListModel<string>[] | [],
    partners: ListModel<string>[] | [],
    // partnerTypes: ListModel<string>[] | []
    currencyList: ListModel<string>[] | []
    employeeList: ListModel<string>[] | []
    /*     contactList: ListModel<string>[] | [] */
    projectTemplates: GroupListModel<string>[] | []
    /*  partnersWithTypes: PartnersWithTypes<string>[] | [] */
    countryList: ListModel<number>[] | [],
    projectLocationNames: ListModel<string>[] | [],
    centarTypes: ListModel<number>[] | [],
}

export interface ProjectBasicInfoRequest {
    projectId: string,
    name: string,
    longName: string,
    number: string,
    departmentId: string,
    tenants: string[] | [],
    articles?: string[] | null,
    description: string,
    isDepartmentProject: boolean,
    centarType: number,
    email?: string | null
}

export const initProjectBasicInfoModel: ProjectBasicInfoRequest = {
    projectId: "",
    name: "",
    longName: "",
    number: "",
    departmentId: "",
    tenants: [],
    articles: [],
    description: "",
    isDepartmentProject: false,
    centarType: -1,
    email: null
}

export interface ProjectBasicInfoResponse {
    projectId: string,
    name: string,
    longName: string,
    number: string,
    corporationId: string,
    departmentId: string,
    description: string,
    tenants: ListModel<string>[] | [],
    articles: ListModel<string>[] | [],
    hasProcedures: boolean,
    hasPlanning: boolean,
    hasLocations: boolean,
    hasResponsibilityAssignment: boolean,
    hasProjectAssignments: boolean,
    hasProjectParticipants: boolean,
    isDepartmentProject: boolean,
    centarType: number,
    email: string | null
}

export interface CreateProjectPlanningRequest extends CreatePlanningTemplateRequest {
    projectId: string;
    sourceTemplateId: string | null;
}

export interface ProjectLocationRequest {
    id: string | null,
    projectId: string,
    projectLocationNameId: string,
    streetAddress: string,
    addressExtension?: string | null;
    city: string,
    state?: string | null,
    postalCode: string,
    country: number,
    isMain: boolean;
    longitude?: number | null,
    latitude?: number | null,
}

export interface ProjectLocation {
    id: string,
    projectId: string,
    projectLocationNameId: string,
    streetAddress: string,
    addressExtension?: string | null;
    city: string,
    state?: string | null,
    country: number,
    postalCode: string,
    isMain: boolean;
    longitude?: number | null,
    latitude?: number | null
}

export interface InvoiceProjectReport {
    companyName?: string | null;
    tenantAbbreviation: string;
    responsibleTechincal: string;
    responsibleFinancial: string;
    invoiceNumber?: string | null;
    invoiceDate?: Date | null;
    procedureName: string;
    startDate?: Date | null;
    endDate?: Date | null;
    total?: number | null;
    salary?: number | null;
    material?: number | null;
    open?: number | null;
    paid?: number | null;
    currency: string;
}

export interface ProjectFinanceReportResponse {
    projectId: string | null;
    periodFrom?: Date | null;
    periodTo?: Date | null;
    projectName?: string | null;
    projectNumber?: string | null;
    incomingInvoices: InvoiceProjectReport[],
    outgoingInvoices: InvoiceProjectReport[]
}

export interface ProcedureReportData {
    name: string,
}

export interface ProjectFinanceReportRequest {
    projectId: string;
    procedures?: string[] | null;
    from?: Date | null;
    to?: Date | null;
}

export interface ProcedureListModel {
    id: string | null;
    name?: string | null;
    contractType?: string | null;
}

export interface ProcedureResponse {
    financialProcedure: Procedure
    financialProcedureResponsibilites: ProjectResponsibilities
    responsibilityHistory: ResponsibilityHistory[];
}

export interface Procedure {
    id?: string | null;
    name?: string | null;
    projectName?: string | null;
    contractNumber?: string | null;
    contractType?: string | null;
    paymentInterval?: number | null;
    invoiceEmail?: string | null;
    ourCostcenterEmail?: string | null;
    noticePeriod?: number | null;
    partnerProjectName?: string | null;
    note?: string | null;
    currency?: string | null;
    amountPerHour?: number | null;
    plannedHours?: number | null;
    totalAmount?: number | null;
    contractStartDate?: Date | null;
    contractEndDate?: Date | null;

    procedureType?: number | null;

    tenant: string;
    tenantResponsiblePerson: string;
    tenantLocation: string;
    tenantLocationBankData: string | null;

    partner: string;
    contact: string;
    partnerLocation: string;
    partnerBankData: string | null;

    files: ProcedureInfoFile[] | [];
}

export interface ProcedureInfoFile {
    parentFileId?: string | null
    id?: string | null
    fileName: string;
    description: string | null;
    fileBase64: string;
    isMain: boolean;
    createdAt: Date | null;
    createdBy: string | null;
    editedAt: Date | null;
    editedBy: string | null;
    position: string;
    additionalFiles?: ProcedureInfoFile[]
}

/*****************************************************************************************************************************************/

// const reservedWindowsNames = [
//     "CON", "PRN", "AUX", "NUL",
//     "COM1", "COM2", "COM3", "COM4", "COM5", "COM6", "COM7", "COM8", "COM9",
//     "LPT1", "LPT2", "LPT3", "LPT4", "LPT5", "LPT6", "LPT7", "LPT8", "LPT9"
// ];

const forbiddenCharsRegex = /[\\\/:\*\?"<>\|]/;

export const ProjectBasicInfoModelScheme = yup.object().shape({
    projectId: yup.string().defined(),
    name: yup.string().noLeadingSpace().required("Name is required.").max(150, "Name is too long.")
        .test(
            "no-special-chars",
            "Name cannot contain any of the following characters: \\ / : * ? \" < > |",
            (value) => value ? !forbiddenCharsRegex.test(value) : true
        ),
    longName: yup.string().noLeadingSpace().required("Name is required.").max(150, "Name is too long."),
    number: yup.string().required("Number is required.").defined().transform((value, originalValue) => String(originalValue)),
    departmentId: yup.string().required("Department is required."),
    tenants: yup.array().min(1, "At least one tenant must be selected.").required("Tenant is required."),
    articles: yup.array().nullable().notRequired(),
    description: yup.string().max(2000, "Description is too long.").defined(),
    email: yup.string().noLeadingSpace().validEmail().nullable(),
    isDepartmentProject: yup.bool().defined(),
    centarType: yup.number().transform((value) => Number.isNaN(value) ? null : value).required("Center type is required.").min(0, "Center type is required.")
})

export interface ProjectResponsibilityResponse {
    id?: string | null;
    message: string;
}

export interface ProjectResponsibilities {
    id?: string | null;
    procedureId?: string | null;
    projectLeadId: string;
    deputyProjectLeadId?: string | null;
    salesPersonId?: string | null;
    accountingContactId?: string | null;
    internalIntermediaryId?: string | null;
    partnerProjectLeadId?: string | null;
    partnerDeputyProjectLeadId?: string | null;
    partnerSalesPersonId?: string | null;
    partnerAccountingContactId?: string | null;
    externalIntermediaryId?: string | null;
}

export interface ResponsibilityHistory {
    id: string,
    role: string,
    isExternal: boolean,
    startDate: Date,
    endDate: Date,
    changedAt: Date,
    changedBy: string,
    responsiblePerson: string
}

export const ProjectResponsibilitiesSchema = yup.object().shape({
    id: yup.string().nullable(),
    procedureId: yup.string().nullable(),
    projectLeadId: yup.string().required("Our technical lead is required."),
    deputyProjectLeadId: yup.string().nullable(),
    salespersonId: yup.string().nullable(),
    accountingContactId: yup.string().nullable(),
    internalIntermediaryId: yup.string().nullable(),
    partnerProjectLeadId: yup.string().nullable(),
    partnerDeputyProjectLeadId: yup.string().nullable(),
    partnerSalesPersonId: yup.string().nullable(),
    partnerAccountingContactId: yup.string().nullable(),
    externalIntermediaryId: yup.string().nullable(),
});

export const projectLocationRequestSchema = yup.object().shape({
    id: yup.string().defined().nullable(),
    projectId: yup.string().defined().min(1, "").required(),
    projectLocationNameId: yup.string().required("Location is required."),
    streetAddress: yup.string().required("Street and number are required."),
    addressExtension: yup.string().noLeadingSpace().nullable(),
    city: yup.string().required("City is required."),
    state: yup.string().nullable(),
    postalCode: yup.string().required("Zip code is required."),
    country: yup.number().positive("Country is required.").integer("Country is required.").required("Country is required."),
    isMain: yup.boolean().defined(),
    longitude: yup.number().nullable(),
    latitude: yup.number().nullable(),
})

export interface CostCenterResponsibilityAssignment {
    id?: string | null,
    projectId?: string | null,
    technicalLeadId: string | null,
    deputyTechnicalLeadId?: string | null,
    resourceAllocationPermittedPersonIds: string[],
}

export interface ResponsibilityAssignmentResponse {
    message: string
}

export const ResponsibilityAssignmentSchema = yup.object().shape({
    id: yup.string().nullable(),
    projectId: yup.string().nullable(),
    technicalLeadId: yup.string().required("Technical lead is required.").nullable(),
    deputyTechnicalLeadId: yup.string().nullable(),
    resourceAllocationPermittedPersonIds: yup.array().defined(),
});

export const initCostCenterResponsibilityAssignmentModel: CostCenterResponsibilityAssignment = {
    id: null,
    projectId: null,
    technicalLeadId: null,
    deputyTechnicalLeadId: null,
    resourceAllocationPermittedPersonIds: [],
}

export interface projectAssignmentModel {
    id?: string | null,
    employeeId: string | null,
    projectId: string | null,
    startDate: Date | null,
    endDate?: Date | null
}

export interface projectBulkAssignmentModel {
    assignments: projectAssignmentModel[]
}

export interface projectAssignmentTableModel {
    id?: string | null,
    employeeId: string | null,
    employee: string,
    project: string,
    projectId: string | null,
    startDate: Date | null,
    endDate?: Date | null
}

export interface getAllProjectAssignmentResponse {
    projectAssignments: projectAssignmentTableModel[]
}

export interface assignmentColumnVisibilityModel {
    id: boolean,
    employeeId: boolean,
    employee: boolean,
    project: boolean,
    startDate: boolean,
    endDate: boolean,
}

export interface assignmentErrors {
    employeeIdValid: boolean,
    employeeMessage: string | '',
    startDateValid: boolean,
    startDateMessage: string | ''
    endDateValid: boolean,
    endDateMessage: string | ''
}

export const assignmentErrorsInit: assignmentErrors = {
    employeeIdValid: true,
    employeeMessage: '',
    startDateValid: true,
    startDateMessage: '',
    endDateValid: true,
    endDateMessage: ''
};

export interface assignmentByEmployeeErrors {
    projectIdValid: boolean,
    projectMessage: string | '',
    startDateValid: boolean,
    startDateMessage: string | ''
    endDateValid: boolean,
    endDateMessage: string | ''
}

export const assignmentByEmployeeErrorsInit: assignmentByEmployeeErrors = {
    projectIdValid: true,
    projectMessage: '',
    startDateValid: true,
    startDateMessage: '',
    endDateValid: true,
    endDateMessage: ''
};

export interface changeProjectAssignmentResponse {
    message: string
}

export interface ProjectParticipantDraft {
    id: string;
    projectId: string;
    participantId: string;
    participantRoleDropdown: ListModel<string> | null;
    participantNameDropdown: ListModel<string> | null;
    companyDropdown: ListModel<string> | null;
    participantRoleName: string;
    position: string;
    comment: string;
    startDate: Dayjs | null;
    endDate: Dayjs | null;
    participantName: string;
    companyNames: string;
    email: string;
    companyPhone: string;
    privatePhone: string;
    specialField: string;
    craft: string;
    isUpdated: boolean;
    isActive: boolean;
}

export const ProcejtParticipantDraftSchema = yup.object().shape({
    comment: yup.string().nullable().notRequired(),
    companyDropdown: yup.object().required("Company is required."),
    participantNameDropdown: yup.object().required("Participant name is required."),
    participantRoleDropdown: yup.object().required("Participant role is required."),
    position: yup.string().noLeadingSpace().notRequired(),
    startDate: yup.mixed().required("Start date is required."),
    endDate: yup.mixed().nullable().test(
        'is-after-start',
        'End date cannot be earlier than start date.',
        function (endDate) {
            const { startDate } = this.parent;
            if (!endDate || !startDate) return true;

            // Handle dayjs objects
            const endDateValue = (endDate && typeof endDate === 'object' && 'isValid' in endDate && typeof endDate.isValid === 'function' && endDate.isValid()) ? (endDate as Dayjs).toDate() : endDate;
            const startDateValue = (startDate && typeof startDate === 'object' && 'isValid' in startDate && typeof startDate.isValid === 'function' && startDate.isValid()) ? (startDate as Dayjs).toDate() : startDate;

            return new Date(endDateValue as string | number | Date) >= new Date(startDateValue as string | number | Date);
        }
    ).notRequired(),
});

export const initialProjectParticipant: ProjectParticipantDraft = {
    id: "",
    projectId: "",
    participantId: "",
    participantRoleDropdown: null,
    participantNameDropdown: null,
    participantRoleName: "",
    companyDropdown: null,
    position: "",
    comment: "",
    startDate: null,
    endDate: null,
    participantName: "",
    companyNames: "",
    email: "",
    companyPhone: "",
    privatePhone: "",
    specialField: "",
    craft: "",
    isUpdated: false,
    isActive: true
};

export interface ProjectParticipant {
    id: string;
    participantId: string;
    participantRoleId: string;
    participantName: string;
    participant: {
        value: string;
        label: string;
        isActive: boolean;
    },
    company: {
        value: string;
        label: string;
        isActive: boolean;
    };
    participantRole: {
        value: string;
        label: string;
        isActive: boolean;
    },
    companyNames: string;
    position: string;
    email: string;
    companyPhone: string;
    privatePhone: string;
    specialField: string;
    craft: string;
    status: string;
    comment: string;
    startDate: Date | null;
    endDate: Date | null;
    isUpdated: boolean;
    isActive: boolean;
    isEdit?: boolean;
    alreadyCreated?: boolean;
    roleError: boolean;
    startDateError: boolean;
}

export interface CreateProjectParticipantsRequest {
    projectId: string;
    participants: ProjectParticipant[];
}

export interface CreateProjectParticipantSingle {
    id: string;
    projectId: string;
    participant: {
        value: string;
        label: string;
        isActive: boolean;
    },
    participantRole: {
        value: string;
        label: string;
        isActive: boolean;
    },
    company: {
        value: string;
        label: string;
        isActive: boolean;
    },
    position: string,
    comment: string,
    startDate: Date | null,
    endDate: Date | null,
    participantName: string,
    companyNames: string,
    email: string,
    companyPhone: string,
    privatePhone: string,
    specialField: string,
    craft: string,
    isActive: boolean
}

export interface GetProjectParticipantsResponse extends CreateProjectParticipantsRequest {
    id: string;
    projectParticipants: ProjectParticipant[];
}

export interface CreateProjectParticipantsResponse {
    id: string;
    message: string;
}

export interface ProjectParticipants {
    projectId: string;
    participants: ProjectParticipant[];
}

export const ProjectParticipantSchema = yup.object().shape({
    id: yup.string().required("ID is required."),
    participantId: yup.string().required("Participant ID is required."),
    projectRoleId: yup.string().required("Project Role ID is required."),
    participantName: yup.string().required("Participant Name is required."),
    companyName: yup.string().required("Company Name is required."),
    position: yup.string().required("Position is required."),
    comment: yup.string().nullable(),
    startDate: yup.date().required("Start date is required."),
    endDate: yup.date().nullable()
});
